/**
 * Context-aware suggestions for the person profile insight panel.
 */
import { isMemorialActive, isMemorialPublished } from './memorial';

export function getPersonDisplayName(person) {
  return [person?.given_names, person?.surname].filter(Boolean).join(' ') || 'This person';
}

const COMPLETION_DEFS = [
  {
    id: 'summary',
    label: 'Add a life summary',
    isMissing: (ctx) => !(ctx.person?.biographical_notes || '').trim(),
    action: {
      type: 'scroll',
      target: '[data-testid="overview-life-summary"]',
      openEdit: true,
      editTestId: 'overview-edit-life-summary',
    },
  },
  {
    id: 'themes',
    label: 'Add known for / remembered for',
    isMissing: (ctx) => !Array.isArray(ctx.person?.life_themes) || ctx.person.life_themes.filter(Boolean).length === 0,
    action: {
      type: 'scroll',
      target: '[data-testid="overview-life-themes"]',
      openEdit: true,
      editTestId: 'overview-edit-life-themes',
    },
  },
  {
    id: 'loved',
    label: 'Add what they loved',
    isMissing: (ctx) => !Array.isArray(ctx.person?.loved_items) || ctx.person.loved_items.length === 0,
    action: {
      type: 'scroll',
      target: '[data-testid="what-they-loved-section"]',
      openEdit: true,
      editTestId: 'add-loved-item-btn',
    },
  },
  {
    id: 'featured',
    label: 'Choose a featured memory',
    isMissing: (ctx) => !(ctx.person?.featured_memory_excerpt || '').trim() && ctx.storyCount === 0,
    action: {
      type: 'scroll',
      target: '[data-testid="overview-featured-memory"]',
      openEdit: true,
      editTestId: 'overview-edit-featured-memory',
    },
  },
  {
    id: 'photo',
    label: 'Add a profile photo',
    isMissing: (ctx) => !ctx.person?.profile_image_url,
    action: { type: 'scroll', target: '[data-testid="upload-photo-btn"]' },
  },
  {
    id: 'voice',
    label: 'Record a voice memory',
    isMissing: (ctx) => ctx.voiceMemoriesCount === 0,
    action: { type: 'navigate', view: 'stories', focus: 'voice' },
  },
  {
    id: 'possession',
    label: 'Add a priceless possession',
    isMissing: (ctx) => ctx.possessionCount === 0,
    action: { type: 'scroll', target: '[data-testid="priceless-possessions-section"], [data-testid="memory-markers-section"]' },
  },
  {
    id: 'family',
    label: 'Link a family connection',
    isMissing: (ctx) => ctx.isGenealogical && ctx.familyConnectionCount === 0,
    action: { type: 'navigate', view: 'family' },
  },
  {
    id: 'memorial-cover',
    label: 'Add a memorial cover image',
    isMissing: (ctx) => ctx.isGenealogical && isMemorialActive(ctx.person) && ctx.memorial && !ctx.memorial.cover_photo_url,
    action: { type: 'memorial' },
  },
];

export function buildCompletionSuggestions(ctx) {
  if (!ctx.canEdit || !ctx.person) return [];
  return COMPLETION_DEFS.filter((def) => def.isMissing(ctx))
    .map(({ id, label, action }) => ({ id, label, action }))
    .slice(0, 6);
}

/** Single highest-priority profile completion step for the overview hero CTA. */
export function pickRecommendedNextAction(ctx) {
  const suggestions = buildCompletionSuggestions(ctx);
  return suggestions[0] || null;
}

export function buildMemoryPrompts(person) {
  const name = getPersonDisplayName(person);
  return [
    `What should people remember about ${name}?`,
    'What object or place reminds you of them?',
    'What story should not be forgotten?',
  ];
}

export function buildMemorialReadiness({ person, memorial }) {
  if (!isMemorialActive(person) || !memorial) return [];
  const items = [];
  const add = (id, label, done, action) => items.push({ id, label, done, action });

  add('cover', 'Cover image', Boolean(memorial.cover_photo_url), { type: 'memorial' });
  add('story', 'Life story', Boolean(memorial.published_biography?.trim()), { type: 'memorial' });
  add('gallery', 'Gallery photos', (memorial.gallery_document_ids || []).length > 0, { type: 'memorial' });
  add('tributes', 'Tributes enabled', memorial.allow_tributes !== false, { type: 'memorial' });
  add('qr', 'QR & keepsakes', Boolean(memorial.slug), { type: 'memorial' });
  add('publish', 'Published publicly', isMemorialPublished(memorial), { type: 'memorial' });

  return items;
}

export function buildFamilyInsights({ person, relationships, isGenealogical }) {
  if (!isGenealogical) return { gaps: [], verificationNote: null };
  const gaps = [];
  if ((relationships?.parents || []).length === 0) gaps.push('Parents not linked yet');
  if ((relationships?.partnerships || []).length === 0 && (relationships?.children || []).length === 0) {
    gaps.push('Partner or children not linked');
  }
  const verificationNote = ['private_placeholder', undefined].includes(person?.identity_status)
    ? 'Connection can be verified when they join MyLegacyBridge.'
    : null;
  return { gaps: gaps.slice(0, 3), verificationNote };
}

export function resolveInsightAction(action, handlers) {
  if (!action) return;
  if (action.type === 'scroll' && action.target) {
    const el = document.querySelector(action.target);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (action.openEdit) {
      window.setTimeout(() => {
        const editSelector = action.editTestId
          ? `[data-testid="${action.editTestId}"]`
          : `${action.target} [data-testid^="overview-edit-"]`;
        document.querySelector(editSelector)?.click();
      }, 350);
    }
    return;
  }
  if (action.type === 'navigate' && handlers.onNavigate) {
    handlers.onNavigate(action.view, { focus: action.focus, story: action.story });
    return;
  }
  if (action.type === 'memorial' && handlers.onOpenMemorial) {
    handlers.onOpenMemorial();
  }
  if (action.type === 'export' && handlers.onExportPdf) {
    handlers.onExportPdf('keepsake');
  }
  if (action.type === 'storybook' && handlers.onOpenStorybook) {
    handlers.onOpenStorybook();
  }
}
